/* @flow */
import * as React from 'react';
import cx from 'classnames';

import { SKIP_URGENT_MESSAGING_INFO_DIALOG_COOKIE } from 'chat/constants/ChatConstants';
import { SplitComposerModal } from 'chat/components/SplitComposer';
import {
  SplitComposerModal as NewComposerModal,
  UrgentMessagingLearnMoreDialog,
} from 'chat/components/FullPageComposer/SplitComposer';
import Button from 'components/Button';
import Cookies from 'lib/Cookies';
import Image from 'components/Image';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButton';
import ModalMenuItem from 'components/ModalMenuItem';
import ModalTrigger from 'components/ModalTrigger';
import Text from 'components/Text';
import Timestamp from 'components/Timestamp';
import Tooltip from 'components/Tooltip';
import { ComposerContext } from 'chat/components/SplitComposer/components/ComposerContext';

import type { Props as ButtonProps } from 'components/Button';
import type { UIEventContext } from 'chat/components/SplitComposer/components/ComposerContext';
import AnnouncementImage from 'assets/images/SplitComposer/announcement.svg';
import ConversationImage from 'assets/images/SplitComposer/conversation.svg';
import UrgentAnnouncementImage from 'assets/images/SplitComposer/urgent.svg';

import type { OrganizationTarget, GroupTarget } from 'entities/Message';

type Props = {
  label?: React.Node,
  className?: ?string,
  hideAnnouncements?: ?boolean,
  onComposeSelection?: ?Function,
  initialTarget?: ?(GroupTarget | OrganizationTarget),
  canViewUrgentMessaging: boolean,
  canViewConversations: boolean,
  currentUserMutedUntil: ?Date,
  hasNewComposer: boolean,
  full?: boolean,
} & ButtonProps;

function MenuItemContent(props: { image: string, title: string, subtext: string }) {
  return (
    <MediaObject center gutter="small">
      <Image src={props.image} />
      <Text lineHeight="xlarge">
        <Text element="div" color="pitch" weight="semibold" size="medium">
          {props.title}
        </Text>
        <Text color="thunder" size="xsmall">
          {props.subtext}
        </Text>
      </Text>
    </MediaObject>
  );
}

export default class TriggerComposerButton extends React.PureComponent<Props, void> {
  getCommonButtonProps(options?: ?{ disabled: boolean }) {
    const { className } = this.props;
    return {
      disabled: options ? options.disabled : false,
      'data-test-selector': 'universal_compose_button',
      className: cx('TriggerComposerButton', className),
      eventKey: 'universal_composer.universal_compose.click',
      eventElementName: 'UniversalComposeButton',
    };
  }

  getComposerModal() {
    return this.props.hasNewComposer ? NewComposerModal : SplitComposerModal;
  }

  renderMessageTypeMenu(uiEventContext: UIEventContext) {
    const { canViewConversations, canViewUrgentMessaging, initialTarget } = this.props;
    const initialTargets = initialTarget ? [initialTarget] : null;
    const skipUrgentMessagingDialog = Cookies.get(SKIP_URGENT_MESSAGING_INFO_DIALOG_COOKIE);
    const ComposerModal = this.getComposerModal();
    const urgentModal = skipUrgentMessagingDialog ? (
      <ComposerModal
        initialTargets={initialTargets}
        messageType="urgent"
        data-test-selector="urgent-composer-modal"
      />
    ) : (
      <UrgentMessagingLearnMoreDialog
        modal={<ComposerModal initialTargets={initialTargets} messageType="urgent" />}
        onRequestHide={() => {}}
        showCheckboxToSkip
        data-test-selector="urgent-learn-more-modal"
      />
    );

    return (
      <Menu width="medium" rowHover>
        <ModalMenuItem
          modal={<ComposerModal initialTargets={initialTargets} />}
          eventKey="universal_composer.message_type_menu.menu_item.click"
          eventProperties={uiEventContext}
          data-test-selector="composer-menu-item-normal-composer">
          {canViewConversations ? (
            <MenuItemContent
              image={AnnouncementImage}
              title="Announcement"
              subtext="Sends separately; members cannot see other replies"
            />
          ) : (
            <MenuItemContent
              image={AnnouncementImage}
              title="Message"
              subtext="Announcements, group conversations, or individual conversation"
            />
          )}
        </ModalMenuItem>
        {canViewConversations && (
          <ModalMenuItem
            modal={<ComposerModal initialTargets={initialTargets} messageType="conversation" />}
            eventKey="universal_composer.message_type_menu.menu_item.click"
            eventProperties={uiEventContext}
            data-test-selector="composer-menu-item-conversation-composer">
            <MenuItemContent
              image={ConversationImage}
              title="Conversation"
              subtext="Send as one group; members can see all replies"
            />
          </ModalMenuItem>
        )}
        {canViewUrgentMessaging && (
          <ModalMenuItem
            modal={urgentModal}
            eventKey="universal_composer.message_type_menu.menu_item.click"
            data-test-selector="composer-menu-item-urgent-composer"
            eventProperties={uiEventContext}>
            <MenuItemContent
              image={UrgentAnnouncementImage}
              title="Urgent Message"
              subtext="Override opt-out preferences and call recipients unreachable by text"
            />
          </ModalMenuItem>
        )}
      </Menu>
    );
  }

  renderMenuButton() {
    const { full, label, ...props } = this.props;

    return (
      <ComposerContext.Consumer>
        {composerContext => {
          return (
            <MenuButton
              {...props}
              {...this.getCommonButtonProps()}
              menu={this.renderMessageTypeMenu(composerContext.getUIEventContext())}
              full={full}>
              {label || null}
            </MenuButton>
          );
        }}
      </ComposerContext.Consumer>
    );
  }

  renderButton(options?: { disabled: boolean }) {
    const { label, ...props } = this.props;
    return (
      <Button {...props} {...this.getCommonButtonProps(options)}>
        {label}
      </Button>
    );
  }

  renderEnabledButton() {
    if (this.props.canViewUrgentMessaging || this.props.canViewConversations) {
      return this.renderMenuButton();
    }
    const ComposerModal = this.getComposerModal();

    return (
      <ModalTrigger
        modal={
          <ComposerModal
            initialTargets={this.props.initialTarget ? [this.props.initialTarget] : null}
          />
        }>
        {this.renderButton()}
      </ModalTrigger>
    );
  }

  renderDisabledButton(currentUserMutedUntil: Date) {
    const timestamp = <Timestamp date={currentUserMutedUntil} format="long" />;
    return (
      <LayerTrigger
        layer={
          <Tooltip>
            <Text align="center" element="div">
              Your messaging privileges have been temporarily suspended until {timestamp}.
            </Text>
          </Tooltip>
        }
        placement="bottom">
        {this.renderButton({ disabled: true })}
      </LayerTrigger>
    );
  }

  render() {
    const { currentUserMutedUntil } = this.props;
    return currentUserMutedUntil
      ? this.renderDisabledButton(currentUserMutedUntil)
      : this.renderEnabledButton();
  }
}
